/* Revalidates cart lines against fresh stock when the cart opens and before checkout.
   Advisory only: if the stock request fails the customer continues; checkout enforces stock. */
(function () {
  'use strict';
  const root = () => window.Shopify?.routes?.root || '/';
  const isCheckoutLink = link => link && /\/checkout\/?$/.test(new URL(link.href, location.origin).pathname);
  let running = null;

  async function fetchStock() {
    const response = await fetch(`${root()}cart?sections=bss-cart-stock&_=${Date.now()}`, { cache: 'no-store' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const html = (await response.json())['bss-cart-stock'];
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const data = JSON.parse(doc.querySelector('[data-bss-cart-stock]').textContent);
    if (!Array.isArray(data.lines)) throw new Error('Invalid stock data');
    return data;
  }

  // Pack components: 1 unit per pack per occurrence. A null map entry is a bigblue-ignore
  // component (unchecked); a missing map, id or unreadable selection fails closed.
  function components(line) {
    if (line.bundle_selection === undefined) return [];
    let ids;
    // Liquid outputs line item properties HTML-escaped (&quot;), so decode before parsing.
    const raw = new DOMParser().parseFromString(String(line.bundle_selection), 'text/html').documentElement.textContent;
    try { ids = JSON.parse(raw).map(value => String(value).split('/').pop()); } catch (_) { ids = null; }
    if (!Array.isArray(ids) || !ids.length) return [{ id: null, count: 1, limit: 0 }];
    const counts = new Map();
    ids.forEach(id => counts.set(id, (counts.get(id) || 0) + 1));
    return [...counts].map(([id, count]) => {
      const value = line.components ? line.components[id] : undefined;
      return { id, count, limit: value === null ? -1 : Number(value ?? 0) };
    });
  }

  // Shares each variant's limit across loose lines first, then pack components, in cart order.
  function plan(lines) {
    const used = new Map();
    const changes = [];
    const ordered = [...lines.filter(line => line.bundle_selection === undefined), ...lines.filter(line => line.bundle_selection !== undefined)];
    ordered.forEach(line => {
      const own = { id: String(line.variant_id), count: 1, limit: Number(line.limit) };
      const parts = components(line);
      let quantity = line.quantity;
      let packLimited = false;
      [own, ...parts].forEach(part => {
        if (!Number.isFinite(part.limit) || part.limit < 0) return;
        const max = Math.floor(Math.max(0, part.limit - (used.get(part.id) || 0)) / part.count);
        if (max < quantity) { quantity = max; packLimited = part !== own; }
      });
      [own, ...parts].forEach(part => used.set(part.id, (used.get(part.id) || 0) + quantity * part.count));
      if (quantity < line.quantity) changes.push({ ...line, newQuantity: quantity, pack: packLimited });
    });
    return changes;
  }

  // bss-stock.js plans the cart with any new item before adding it, so this file never has to undo an add.
  window.BuddhaCartStock = { fetchStock, plan };

  // Silently drops what is no longer in stock. Resolves true when the cart was changed.
  function check() {
    if (running) return running;
    running = (async () => {
      let data;
      try { data = await fetchStock(); } catch (_) { return false; }
      let changed = false;
      for (const change of plan(data.lines)) {
        try {
          const response = await fetch(`${root()}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: change.key, quantity: change.newQuantity })
          });
          changed ||= response.ok;
        } catch (_) { /* Checkout still enforces stock. */ }
      }
      if (changed) document.dispatchEvent(new CustomEvent('theme:cartchanged', { bubbles: true, cancelable: false }));
      return changed;
    })().finally(() => { running = null; });
    return running;
  }

  const approved = new WeakSet();

  async function guard(event, element, proceed) {
    if (approved.has(element)) { approved.delete(element); return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    // The cart form would resubmit the old quantities, so after a change go straight to checkout.
    if (await check()) { location.href = `${root()}checkout`; return; }
    approved.add(element);
    proceed();
  }

  document.addEventListener('submit', event => {
    const form = event.target;
    const submitter = event.submitter;
    if (!(form instanceof HTMLFormElement) || submitter?.name !== 'checkout') return;
    guard(event, form, () => form.requestSubmit(submitter));
  }, true);

  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest?.('a[href]');
    if (!isCheckoutLink(link)) return;
    guard(event, link, () => link.click());
  }, true);

  document.addEventListener('theme:open-cart-drawer', () => { check(); });
  // Loaded with defer, so the cart page markup is already parsed.
  if (document.querySelector('[data-section-type="cart-template"]')) check();
})();
